import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export interface MentorshipEnrollment {
  id: string;
  user_email: string;
  user_id: string | null;
  product_id: string | null;
  order_id: string | null;
  enrolled_at: string;
  payment_verified: boolean;
  access_granted: boolean;
  product_name?: string;
  product_type?: string;
  product_price?: number;
}

export function useMentorshipEnrollment(user: User | null) {
  const [enrollment, setEnrollment] = useState<MentorshipEnrollment | null>(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchEnrollment();
    } else {
      setEnrollment(null);
      setIsEnrolled(false);
      setIsLoading(false);
    }
  }, [user]);

  const fetchEnrollment = async () => {
    if (!user) return;

    try {
      // Check for mentorship enrollment by user_id OR email
      const { data, error } = await supabase
        .from('user_enrollments')
        .select(`
          *,
          products:product_id (
            name,
            type,
            price
          )
        `)
        .or(`user_id.eq.${user.id},user_email.eq.${user.email}`)
        .eq('payment_verified', true)
        .eq('access_granted', true)
        .order('enrolled_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching enrollment:', error);
        setIsEnrolled(false);
        setEnrollment(null);
      } else if (data) {
        // Check if this is a mentorship enrollment
        const product = data.products as { name: string; type: string; price: number } | null;
        const isMentorship = product?.type === 'mentorship';
        
        setEnrollment({
          id: data.id,
          user_email: data.user_email,
          user_id: data.user_id,
          product_id: data.product_id,
          order_id: data.order_id,
          enrolled_at: data.enrolled_at,
          payment_verified: data.payment_verified,
          access_granted: data.access_granted,
          product_name: product?.name,
          product_type: product?.type,
          product_price: product?.price,
        });
        setIsEnrolled(isMentorship);
      } else {
        setEnrollment(null);
        setIsEnrolled(false);
      }
    } catch (err) {
      console.error('Error fetching enrollment:', err);
      setIsEnrolled(false);
      setEnrollment(null);
    } finally {
      setIsLoading(false);
    }
  };

  return { enrollment, isEnrolled, isLoading, refetch: fetchEnrollment };
}
